import { parseKbBindingsFromContext, type KbWikiBinding } from "./hermes-api";

export type CapabilityAccess = "read" | "write";
export type MaterialsBinding =
  | { scope: "enterprise"; access: CapabilityAccess }
  | { scope: "brand"; brand_id: number; access: CapabilityAccess };
export type BrandToolBinding = { brand_id: number; access: CapabilityAccess };

export type CapabilityDraft = {
  fetchOn: boolean;
  materials: MaterialsBinding[];
  kbBindings: KbWikiBinding[];
  journey: BrandToolBinding[];
  brandSite: BrandToolBinding[];
};

export type CapabilityChipLabels = {
  accountName: string;
  brands: Array<{ id: number; name: string }>;
  /** 企业 Wiki id；chip 前缀用账户名 */
  enterpriseWikiId?: number;
  /** brand_id → wiki_id；chip 前缀用品牌名 */
  brandWikiByBrandId?: Record<number, number>;
};

export type CapabilityRosterInput = {
  accountName: string;
  brands: Array<{ id: number; name: string }>;
  fetchOn: boolean;
  materials: MaterialsBinding[];
  kb: Array<{ wiki_id: number; access: CapabilityAccess; label: string }>;
  journey: BrandToolBinding[];
  brandSite: BrandToolBinding[];
};

const MATERIALS_ACCESS_ZH: Record<CapabilityAccess, string> = {
  read: "只读",
  write: "完全控制",
};

const BRAND_TOOL_ACCESS_ZH: Record<CapabilityAccess, string> = {
  read: "只读",
  write: "完全访问",
};

function brandName(labels: CapabilityChipLabels, brandId: number): string {
  return labels.brands.find((b) => b.id === brandId)?.name ?? `品牌 ${brandId}`;
}

function kbChipPrefix(labels: CapabilityChipLabels, wikiId: number): string {
  if (labels.enterpriseWikiId === wikiId) return labels.accountName;

  for (const brand of labels.brands) {
    if (labels.brandWikiByBrandId?.[brand.id] === wikiId) return brand.name;
  }

  return labels.accountName;
}

/** 从会话 context 解析资源库绑定；忽略非法或重复 scope。 */
export function parseMaterialsBindings(
  context: Record<string, unknown> | null | undefined,
): MaterialsBinding[] {
  const raw = context?.materials_bindings;
  if (!Array.isArray(raw)) return [];

  const bindings = new Map<string, MaterialsBinding>();
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const record = entry as Record<string, unknown>;
    const access = record.access;
    if (access !== "read" && access !== "write") continue;

    const scope = record.scope;
    if (scope === "enterprise") {
      bindings.set("enterprise", { scope: "enterprise", access });
      continue;
    }
    if (scope === "brand") {
      const brandId = Number(record.brand_id);
      if (!Number.isInteger(brandId) || brandId <= 0) continue;
      bindings.set(`brand:${brandId}`, { scope: "brand", brand_id: brandId, access });
    }
  }
  return Array.from(bindings.values());
}

/** 从会话 context 解析旅程 / 官网品牌绑定。 */
export function parseBrandToolBindings(
  context: Record<string, unknown> | null | undefined,
  key: "journey_bindings" | "brand_site_bindings",
): BrandToolBinding[] {
  const raw = context?.[key];
  if (!Array.isArray(raw)) return [];

  const bindings = new Map<number, BrandToolBinding>();
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const record = entry as Record<string, unknown>;
    const brandId = Number(record.brand_id);
    const access = record.access;
    if (!Number.isInteger(brandId) || brandId <= 0) continue;
    if (access !== "read" && access !== "write") continue;
    // 已有 write 绑定时忽略后续 write，避免前端草稿短暂双 write。
    if (access === "write") {
      const existingWrite = Array.from(bindings.values()).some((b) => b.access === "write");
      if (existingWrite) continue;
    }
    bindings.set(brandId, { brand_id: brandId, access });
  }
  return Array.from(bindings.values());
}

/** 联网访问开 = skills 不含 no_fetch（与后端一致）。 */
export function fetchOnFromSkills(skills: string[] | null | undefined): boolean {
  return !(skills ?? []).includes("no_fetch");
}

/** 普通新对话默认只读绑定；BrandHub 新对话额外开启当前品牌四项只读。无企业 Wiki 时不编造 wiki_id。 */
export function buildDefaultCapabilityDraft(args: {
  enterpriseWikiId?: number | null;
  brandId: number | null;
  brandWikiId?: number;
}): CapabilityDraft {
  const materials: MaterialsBinding[] = [{ scope: "enterprise", access: "read" }];
  const kbBindings: KbWikiBinding[] = [];
  if (args.enterpriseWikiId != null && args.enterpriseWikiId > 0) {
    kbBindings.push({ wiki_id: args.enterpriseWikiId, access: "read" });
  }
  const journey: BrandToolBinding[] = [];
  const brandSite: BrandToolBinding[] = [];

  if (args.brandId != null && args.brandId > 0) {
    materials.push({ scope: "brand", brand_id: args.brandId, access: "read" });
    if (args.brandWikiId != null && args.brandWikiId > 0) {
      kbBindings.push({ wiki_id: args.brandWikiId, access: "read" });
    }
    journey.push({ brand_id: args.brandId, access: "read" });
    brandSite.push({ brand_id: args.brandId, access: "read" });
  }

  return {
    fetchOn: true,
    materials,
    kbBindings,
    journey,
    brandSite,
  };
}

/** 从 Wiki 列表解析企业库与当前品牌库，再套默认只读草稿。 */
export function buildDefaultCapabilityDraftFromWikis(
  wikis: Array<{ id: number; kind: string; brand_id: number | null }>,
  brandId: number | null,
): CapabilityDraft {
  const enterprise = wikis.find((wiki) => wiki.kind === "enterprise");
  const brandWiki =
    brandId != null && brandId > 0
      ? wikis.find((wiki) => wiki.kind === "brand" && wiki.brand_id === brandId)
      : undefined;
  return buildDefaultCapabilityDraft({
    enterpriseWikiId: enterprise?.id,
    brandId: brandId != null && brandId > 0 ? brandId : null,
    brandWikiId: brandWiki?.id,
  });
}

function bindingSnapshot(value: unknown): string {
  return JSON.stringify(value);
}

export function capabilityDraftsEqual(a: CapabilityDraft, b: CapabilityDraft): boolean {
  return (
    a.fetchOn === b.fetchOn &&
    bindingSnapshot(a.materials) === bindingSnapshot(b.materials) &&
    bindingSnapshot(a.kbBindings) === bindingSnapshot(b.kbBindings) &&
    bindingSnapshot(a.journey) === bindingSnapshot(b.journey) &&
    bindingSnapshot(a.brandSite) === bindingSnapshot(b.brandSite)
  );
}

/** 新对话占位：企业资源库只读、尚无 Wiki（listWikis 完成前）。 */
export function unseededCapabilityDraft(): CapabilityDraft {
  return buildDefaultCapabilityDraft({ brandId: null });
}

/**
 * 晚到的默认播种是否覆盖当前草稿。
 * 仅当当前仍是占位（用户未改面板）且播种结果与占位不同时才覆盖，避免丢掉进行中的编辑。
 */
export function shouldApplySeededCapabilityDraft(
  current: CapabilityDraft,
  seeded: CapabilityDraft,
  placeholder: CapabilityDraft = unseededCapabilityDraft(),
): boolean {
  if (!capabilityDraftsEqual(current, placeholder)) return false;
  return !capabilityDraftsEqual(current, seeded);
}

/** AI 智能录入：目标 Wiki 完全控制，企业资源库保持只读，联网访问保持开。 */
export function applyKnowledgeEntryOverride(
  base: CapabilityDraft,
  { wikiId }: { wikiId: number },
): CapabilityDraft {
  const kbBindings = [
    ...base.kbBindings.filter((row) => row.wiki_id !== wikiId),
    { wiki_id: wikiId, access: "write" as const },
  ];
  const materials: MaterialsBinding[] = [
    { scope: "enterprise", access: "read" },
    ...base.materials.filter((row) => row.scope !== "enterprise"),
  ];
  return {
    ...base,
    fetchOn: true,
    materials,
    kbBindings,
  };
}

/** 从已存会话还原草稿；不要用默认 builder 覆盖。 */
export function capabilityDraftFromChat(input: {
  context?: Record<string, unknown> | null;
  skills?: string[] | null;
}): CapabilityDraft {
  return {
    fetchOn: fetchOnFromSkills(input.skills),
    materials: parseMaterialsBindings(input.context),
    kbBindings: parseKbBindingsFromContext(input.context),
    journey: parseBrandToolBindings(input.context, "journey_bindings"),
    brandSite: parseBrandToolBindings(input.context, "brand_site_bindings"),
  };
}

/** 写入 createChat / updateChat 的绑定字段（不含 mode 等创作键）。 */
export function capabilityContextFields(draft: CapabilityDraft): {
  kb_bindings: KbWikiBinding[];
  materials_bindings: MaterialsBinding[];
  journey_bindings: BrandToolBinding[];
  brand_site_bindings: BrandToolBinding[];
} {
  return {
    kb_bindings: draft.kbBindings,
    materials_bindings: draft.materials,
    journey_bindings: draft.journey,
    brand_site_bindings: draft.brandSite,
  };
}

/**
 * 企业知识库 id：catalog 只负责标题/映射。
 * catalog 未到时，回退到 draft 里尚未映射为品牌库的 wiki_id，避免把已有绑定显示成「不选」并锁死档位。
 */
export function resolveEnterpriseWikiId(
  draft: Pick<CapabilityDraft, "kbBindings">,
  catalogId?: number | null,
  brandWikiByBrandId: Record<number, number> = {},
): number | null {
  if (catalogId != null && catalogId > 0) return catalogId;
  const mappedBrandWikiIds = new Set(Object.values(brandWikiByBrandId));
  const fallback = draft.kbBindings.find((row) => !mappedBrandWikiIds.has(row.wiki_id));
  return fallback?.wiki_id ?? null;
}

/**
 * 品牌知识库 id：只在 catalog 已映射该品牌时使用；未映射时不从 draft 猜 wiki。
 */
export function resolveBrandWikiId(
  _draft: Pick<CapabilityDraft, "kbBindings">,
  catalogId?: number | null,
): number | null {
  if (catalogId != null && catalogId > 0) return catalogId;
  return null;
}

/** 创作会话 context：先铺绑定，再铺 mode（后写的 mode 不被覆盖）。 */
export function withJourneyAuthoringMode<T extends Record<string, unknown>>(
  draft: CapabilityDraft,
  modeContext: T,
): ReturnType<typeof capabilityContextFields> & T {
  return { ...capabilityContextFields(draft), ...modeContext };
}

/** 官网创作会话：绑定跟草稿走，mode / brand_site_id 后写以免被覆盖。 */
export function withBrandSiteAuthoringMode<T extends Record<string, unknown>>(
  draft: CapabilityDraft,
  modeContext: T,
): ReturnType<typeof capabilityContextFields> & T {
  return { ...capabilityContextFields(draft), ...modeContext };
}

const NO_FETCH_SKILL = "no_fetch";
const KB_SKILL = "kb";

/** 联网开则不含 no_fetch；关则带上。有 kb 绑定时保留 kb skill。 */
export function persistSkillsForDraft(draft: CapabilityDraft, skills: string[] = []): string[] {
  const withoutGate = skills.filter((skill) => skill !== NO_FETCH_SKILL && skill !== KB_SKILL);
  const withKb =
    draft.kbBindings.length > 0 ? [KB_SKILL, ...withoutGate] : withoutGate;
  return draft.fetchOn ? withKb : [...withKb, NO_FETCH_SKILL];
}

/** 旅程 / 官网档位；write 时把其它品牌的 write 降为 read。access=null 表示不选。 */
export function setBrandToolAccess(
  draft: CapabilityDraft,
  tool: "journey" | "brandSite",
  brandId: number,
  access: CapabilityAccess | null,
): CapabilityDraft {
  const current = draft[tool];
  let nextList: BrandToolBinding[];

  if (access == null) {
    nextList = current.filter((row) => row.brand_id !== brandId);
  } else if (access === "write") {
    const seen = new Set<number>();
    nextList = current.map((row) => {
      seen.add(row.brand_id);
      if (row.brand_id === brandId) return { brand_id: brandId, access: "write" as const };
      if (row.access === "write") return { ...row, access: "read" as const };
      return row;
    });
    if (!seen.has(brandId)) nextList.push({ brand_id: brandId, access: "write" });
  } else {
    const exists = current.some((row) => row.brand_id === brandId);
    nextList = exists
      ? current.map((row) => (row.brand_id === brandId ? { ...row, access: "read" as const } : row))
      : [...current, { brand_id: brandId, access: "read" }];
  }

  return { ...draft, [tool]: nextList };
}

/** 企业 / 品牌资源库档位；access=null 表示不选。 */
export function setMaterialsAccess(
  draft: CapabilityDraft,
  target: { scope: "enterprise" } | { scope: "brand"; brand_id: number },
  access: CapabilityAccess | null,
): CapabilityDraft {
  const matches = (row: MaterialsBinding) =>
    target.scope === "enterprise"
      ? row.scope === "enterprise"
      : row.scope === "brand" && row.brand_id === target.brand_id;

  if (access == null) {
    return { ...draft, materials: draft.materials.filter((row) => !matches(row)) };
  }

  const nextRow: MaterialsBinding =
    target.scope === "enterprise"
      ? { scope: "enterprise", access }
      : { scope: "brand", brand_id: target.brand_id, access };
  const exists = draft.materials.some(matches);
  return {
    ...draft,
    materials: exists
      ? draft.materials.map((row) => (matches(row) ? nextRow : row))
      : [...draft.materials, nextRow],
  };
}

/** 知识库 Wiki 档位；access=null 表示不选。 */
export function setKbWikiAccess(
  draft: CapabilityDraft,
  wikiId: number,
  access: CapabilityAccess | null,
): CapabilityDraft {
  if (access == null) {
    return { ...draft, kbBindings: draft.kbBindings.filter((row) => row.wiki_id !== wikiId) };
  }
  const exists = draft.kbBindings.some((row) => row.wiki_id === wikiId);
  return {
    ...draft,
    kbBindings: exists
      ? draft.kbBindings.map((row) => (row.wiki_id === wikiId ? { wiki_id: wikiId, access } : row))
      : [...draft.kbBindings, { wiki_id: wikiId, access }],
  };
}

/** 企业级联网访问开关。 */
export function setFetchOn(draft: CapabilityDraft, fetchOn: boolean): CapabilityDraft {
  return { ...draft, fetchOn };
}

/** 按 capabilityChips 顺序把第 index 项设为不选（联网访问则关）。 */
export function dismissCapabilityChip(draft: CapabilityDraft, index: number): CapabilityDraft {
  let cursor = 0;
  if (draft.fetchOn) {
    if (cursor === index) return setFetchOn(draft, false);
    cursor += 1;
  }
  for (const row of draft.materials) {
    if (cursor === index) {
      return setMaterialsAccess(
        draft,
        row.scope === "enterprise"
          ? { scope: "enterprise" }
          : { scope: "brand", brand_id: row.brand_id },
        null,
      );
    }
    cursor += 1;
  }
  for (const row of draft.kbBindings) {
    if (cursor === index) return setKbWikiAccess(draft, row.wiki_id, null);
    cursor += 1;
  }
  for (const row of draft.journey) {
    if (cursor === index) return setBrandToolAccess(draft, "journey", row.brand_id, null);
    cursor += 1;
  }
  for (const row of draft.brandSite) {
    if (cursor === index) return setBrandToolAccess(draft, "brandSite", row.brand_id, null);
    cursor += 1;
  }
  return draft;
}

/** 已开启能力项数量（与 capabilityChips 长度一致）。 */
export function capabilityCount(draft: CapabilityDraft): number {
  return (
    (draft.fetchOn ? 1 : 0) +
    draft.materials.length +
    draft.kbBindings.length +
    draft.journey.length +
    draft.brandSite.length
  );
}

/** 输入框下方能力按钮文案：未选时「+ 能力」，已选时「能力 x N」。 */
export function capabilityCountLabel(count: number): string {
  if (count <= 0) return "+ 能力";
  return `能力 x ${count}`;
}

/** 输入框下方 scope chip 文案（按企业 / 品牌收束）。 */
export function capabilityChips(
  draft: CapabilityDraft,
  labels: CapabilityChipLabels,
): string[] {
  const chips: string[] = [];
  const account = labels.accountName;

  if (draft.fetchOn) {
    chips.push(`${account} · 联网访问`);
  }

  for (const binding of draft.materials) {
    if (binding.scope === "enterprise") {
      chips.push(`${account} · 资源库${MATERIALS_ACCESS_ZH[binding.access]}`);
    } else {
      chips.push(`${brandName(labels, binding.brand_id)} · 资源库${MATERIALS_ACCESS_ZH[binding.access]}`);
    }
  }

  for (const binding of draft.kbBindings) {
    const prefix = kbChipPrefix(labels, binding.wiki_id);
    chips.push(`${prefix} · 知识库${MATERIALS_ACCESS_ZH[binding.access]}`);
  }

  for (const binding of draft.journey) {
    chips.push(
      `${brandName(labels, binding.brand_id)} · 客户旅程${BRAND_TOOL_ACCESS_ZH[binding.access]}`,
    );
  }

  for (const binding of draft.brandSite) {
    chips.push(
      `${brandName(labels, binding.brand_id)} · 官网${BRAND_TOOL_ACCESS_ZH[binding.access]}`,
    );
  }

  return chips;
}

/** 注入 Agent 指令的人话能力名单；禁止英文工具名。 */
export function capabilityRosterZh(input: CapabilityRosterInput): string {
  const lines: string[] = [];
  const account = input.accountName;

  lines.push(input.fetchOn ? `${account} · 联网访问：开` : `${account} · 联网访问：关`);

  for (const binding of input.materials) {
    if (binding.scope === "enterprise") {
      lines.push(`${account} · 企业资源库 · ${MATERIALS_ACCESS_ZH[binding.access]}`);
    } else {
      const name = brandName({ accountName: account, brands: input.brands }, binding.brand_id);
      lines.push(`${name} · 资源库 · ${MATERIALS_ACCESS_ZH[binding.access]}`);
    }
  }

  for (const row of input.kb) {
    lines.push(`${row.label} · ${MATERIALS_ACCESS_ZH[row.access]}`);
  }

  for (const binding of input.journey) {
    const name = brandName({ accountName: account, brands: input.brands }, binding.brand_id);
    lines.push(`${name} · 客户旅程手册 · ${BRAND_TOOL_ACCESS_ZH[binding.access]}`);
  }

  for (const binding of input.brandSite) {
    const name = brandName({ accountName: account, brands: input.brands }, binding.brand_id);
    lines.push(`${name} · 官网 · ${BRAND_TOOL_ACCESS_ZH[binding.access]}`);
  }

  return lines.join("\n");
}
